import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

export const Route = createFileRoute('/unsubscribe')({
  head: () => ({
    meta: [
      { title: 'Unsubscribe — The Hospitality Reserve' },
      { name: 'description', content: 'Unsubscribe from non-essential emails from The Hospitality Reserve. Operational notices about your engagements will still be delivered.' },
      { name: 'robots', content: 'noindex,nofollow' },
    ],
    links: [{ rel: 'canonical', href: 'https://thehospitalityreserve.com/unsubscribe' }],
  }),
  component: UnsubscribePage,
})

type Status = 'loading' | 'ready' | 'already' | 'invalid' | 'submitting' | 'done' | 'error'

function UnsubscribePage() {
  const [status, setStatus] = useState<Status>('loading')
  const [token, setToken] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('token')
    if (!t) {
      setStatus('invalid')
      return
    }
    setToken(t)
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then(async (r) => {
        const body = await r.json().catch(() => ({}))
        if (!r.ok) {
          setStatus('invalid')
          return
        }
        if (body.valid === true) setStatus('ready')
        else if (body.reason === 'already_unsubscribed') setStatus('already')
        else setStatus('invalid')
      })
      .catch(() => setStatus('invalid'))
  }, [])

  async function confirm() {
    if (!token) return
    setStatus('submitting')
    try {
      const r = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const body = await r.json().catch(() => ({}))
      if (!r.ok) {
        setErrorMsg(body.error || 'Something went wrong.')
        setStatus('error')
        return
      }
      if (body.success || body.reason === 'already_unsubscribed') setStatus('done')
      else setStatus('error')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full border rounded-2xl p-8 bg-card text-card-foreground shadow-sm">
        <h1 className="text-2xl font-semibold mb-3">Email preferences</h1>
        {status === 'loading' && <p className="text-muted-foreground">Checking your link…</p>}
        {status === 'invalid' && (
          <p className="text-muted-foreground">
            This unsubscribe link is invalid or expired. If you keep receiving unwanted emails,
            update your notification preferences while signed in.
          </p>
        )}
        {status === 'already' && (
          <p className="text-muted-foreground">
            You're already unsubscribed from these emails.
          </p>
        )}
        {status === 'ready' && (
          <>
            <p className="text-muted-foreground mb-6">
              Click confirm to stop receiving emails from The Hospitality Reserve Staff at this address. You
              can still sign in to manage your account and re-enable emails any time.
            </p>
            <button
              onClick={confirm}
              className="w-full rounded-md bg-foreground text-background py-2.5 font-medium hover:opacity-90 transition"
            >
              Confirm unsubscribe
            </button>
          </>
        )}
        {status === 'submitting' && <p className="text-muted-foreground">Updating…</p>}
        {status === 'done' && (
          <p className="text-muted-foreground">
            You've been unsubscribed. Account-critical messages (booking confirmations, payouts)
            will still be delivered where required.
          </p>
        )}
        {status === 'error' && (
          <p className="text-destructive">{errorMsg ?? 'Something went wrong. Please try again.'}</p>
        )}
      </div>
    </div>
  )
}
